import { useState, useEffect, useCallback, useRef } from 'react';
import TopNav from './TopNav';
import { useToast } from '../Toast';
import WheelCountCell from '../WheelCountCell';
import { compressImage } from '../imageCompress';

// Kegs are compared in 1/6-bbl equivalents: a 1/2 bbl = 3 x 1/6 bbl.
const kegEquiv = (sixth, half) => (parseFloat(sixth) || 0) + 3 * (parseFloat(half) || 0);

const LOCATIONS = [
  { id: 'midtown',    label: 'Midtown' },
  { id: 'power_mill', label: 'Power Mill' },
  { id: 'northside',  label: 'Northside' },
  { id: 'tampa',      label: 'Tampa' },
];

const STORAGE_AREAS = {
  midtown:    { cans: ['Can Cooler', 'Walk-in'],                              kegs: ['Walk-in'] },
  northside:  { cans: ['Can Cooler', 'Walk-in'],                              kegs: ['Walk-in'] },
  power_mill: { cans: ['Can Cooler', 'Storage Refrigerator', 'Walk-in'],     kegs: ['Kegerator', 'Restock Fridge', 'Walk-in'] },
  tampa:      { cans: ['Can Cooler', 'Walk-in'],                              kegs: ['Walk-in'] },
};

const today = () => new Date().toISOString().slice(0, 10);
// Global discrepancy floors: cans in 4-packs, kegs in 1/6-bbl equivalents.
const DEFAULT_THRESHOLDS = { four_pack_threshold: 3, keg_sixth_equiv_threshold: 3 };

function NavBar({ onBack }) {
  return (
    <TopNav />
  );
}

// ── Count Tab ──────────────────────────────────────────────────────────────

function CountTab({ user, thresholds, canUpload, onDirtyChange }) {
  const toast = useToast();
  const [step, setStep] = useState('location'); // location | area | count | review | done
  const [location, setLocation] = useState(null);
  const [sessionDate, setSessionDate] = useState(today());
  const [beers, setBeers] = useState([]);
  const [counts, setCounts] = useState({});
  const [previous, setPrevious] = useState(null);
  const [delivered, setDelivered] = useState({});
  const [deliveryCount, setDeliveryCount] = useState(0);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentArea, setCurrentArea] = useState(null); // { name, type: 'cans'|'kegs' }
  const [expandedBeer, setExpandedBeer] = useState(null);
  const [photos, setPhotos] = useState({}); // beer_id -> File (optional recount photo for a flagged item)

  // Dirty = in area/count steps with at least one count entered
  const isDirty = (step === 'area' || step === 'count') && Object.values(counts).some(d =>
    Object.values(d.cans || {}).some(v => v !== '' && v !== undefined) ||
    Object.values(d.kegs || {}).some(v =>
      (v.sixth_bbl !== '' && v.sixth_bbl !== undefined) ||
      (v.half_bbl  !== '' && v.half_bbl  !== undefined)
    )
  );
  const isDirtyRef = useRef(isDirty);
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);
  useEffect(() => { onDirtyChange?.(isDirty); }, [isDirty, onDirtyChange]);

  // Warn on browser refresh / tab close
  useEffect(() => {
    const handler = e => { if (isDirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const selectLocation = useCallback(async (loc) => {
    setLocation(loc);
    const [beersRes, prevRes] = await Promise.all([
      fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-beers?location=${loc.id}`, { credentials: 'include' }),
      fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-inventory/latest/${loc.id}`, { credentials: 'include' }),
    ]);
    const beerList = await beersRes.json();
    const prev = await prevRes.json();
    setBeers(beerList);
    setPrevious(prev);

    // Fetch deliveries since the last session to factor into discrepancy baseline
    const deliveredTotals = {};
    let numDeliveries = 0;
    if (prev?.session_date) {
      const delRes = await fetch(
        `${process.env.REACT_APP_API_URL || ''}/api/taproom-deliveries?location=${loc.id}&since=${prev.session_date}`,
        { credentials: 'include' }
      );
      const deliveries = await delRes.json();
      numDeliveries = deliveries.length;
      for (const delivery of deliveries) {
        for (const item of delivery.items || []) {
          if (!item.beer_id) continue;
          if (!deliveredTotals[item.beer_id]) deliveredTotals[item.beer_id] = { cases: 0, sixth_bbl: 0, half_bbl: 0 };
          deliveredTotals[item.beer_id].cases     += parseFloat(item.cases)     || 0;
          deliveredTotals[item.beer_id].sixth_bbl += parseFloat(item.sixth_bbl) || 0;
          deliveredTotals[item.beer_id].half_bbl  += parseFloat(item.half_bbl)  || 0;
        }
      }
    }
    setDelivered(deliveredTotals);
    setDeliveryCount(numDeliveries);

    const locAreas = STORAGE_AREAS[loc.id] || { cans: [], kegs: [] };
    const init = {};
    beerList.forEach(b => {
      const cans = {};
      locAreas.cans.forEach(a => { cans[a] = ''; });
      const kegs = {};
      locAreas.kegs.forEach(a => { kegs[a] = { sixth_bbl: '', half_bbl: '' }; });
      init[b.id] = { cans, kegs };
    });
    setCounts(init);
    setStep('area');
  }, []);

  const setCanCount = (beerId, area, value) =>
    setCounts(prev => ({ ...prev, [beerId]: { ...prev[beerId], cans: { ...prev[beerId].cans, [area]: value } } }));

  const setKegCount = (beerId, area, field, value) =>
    setCounts(prev => ({ ...prev, [beerId]: { ...prev[beerId], kegs: {
      ...prev[beerId].kegs, [area]: { ...prev[beerId].kegs[area], [field]: value },
    }}}));

  const getTotals = (beerId) => {
    const d = counts[beerId] || {};
    return {
      four_pack: Object.values(d.cans || {}).reduce((s, v) => s + (parseFloat(v) || 0), 0),
      sixth_bbl: Object.values(d.kegs || {}).reduce((s, v) => s + (parseFloat(v.sixth_bbl) || 0), 0),
      half_bbl:  Object.values(d.kegs || {}).reduce((s, v) => s + (parseFloat(v.half_bbl)  || 0), 0),
    };
  };

  // Floor for a beer in each dimension: per-SKU override → global → default.
  const canFloor = (beer) =>
    beer.disc_fourpack_threshold != null ? parseFloat(beer.disc_fourpack_threshold)
      : (thresholds.four_pack_threshold ?? DEFAULT_THRESHOLDS.four_pack_threshold);
  const kegFloor = (beer) =>
    beer.disc_keg_equiv_threshold != null ? parseFloat(beer.disc_keg_equiv_threshold)
      : (thresholds.keg_sixth_equiv_threshold ?? DEFAULT_THRESHOLDS.keg_sixth_equiv_threshold);

  // Expected ceiling = last count + deliveries since (cans in 4-packs; kegs in 1/6 equiv).
  const expectedCans = (beerId) => {
    const p = parseFloat(previous?.counts?.[beerId]?.four_pack) || 0;
    const d = (parseFloat(delivered[beerId]?.cases) || 0) * 6;
    return p + d;
  };
  const expectedKegs = (beerId) => {
    const pc = previous?.counts?.[beerId] || {};
    const del = delivered[beerId] || {};
    return kegEquiv(pc.sixth_bbl, pc.half_bbl) + kegEquiv(del.sixth_bbl, del.half_bbl);
  };

  // 'yellow' (counted 0) | 'red' (above ceiling) | 'orange' (dropped > floor) | 'default'
  const dimState = (expected, counted, floor) => {
    if (expected === 0) return 'default';
    if (counted === 0) return 'yellow';
    if (counted > expected) return 'red';
    if (expected - counted > floor) return 'orange';
    return 'default';
  };
  const cansState = (beer) => dimState(expectedCans(beer.id), getTotals(beer.id).four_pack, canFloor(beer));
  const kegsState = (beer) => {
    const t = getTotals(beer.id);
    return dimState(expectedKegs(beer.id), kegEquiv(t.sixth_bbl, t.half_bbl), kegFloor(beer));
  };

  // One entry per flagged beer, with its cans/keg reasons (for the review step).
  const getFlaggedBeers = () => beers.flatMap(b => {
    const reasons = [];
    const c = cansState(b);
    if (c === 'orange' || c === 'red') {
      const exp = expectedCans(b.id), cur = getTotals(b.id).four_pack;
      reasons.push({ dim: 'Cans (4-packs)', state: c, expected: exp, curr: cur, diff: cur - exp });
    }
    const k = kegsState(b);
    if (k === 'orange' || k === 'red') {
      const t = getTotals(b.id);
      const exp = expectedKegs(b.id), cur = kegEquiv(t.sixth_bbl, t.half_bbl);
      reasons.push({ dim: 'Kegs (1/6 equiv)', state: k, expected: exp, curr: cur, diff: cur - exp });
    }
    return reasons.length ? [{ id: b.id, name: b.name, reasons }] : [];
  });

  const handleSubmit = async () => {
    setSubmitting(true);
    const locAreas = STORAGE_AREAS[location.id] || { cans: [], kegs: [] };
    const payload = [];
    for (const b of beers) {
      const d = counts[b.id] || {};
      for (const area of locAreas.cans) {
        const fp = parseFloat(d.cans?.[area]) || 0;
        if (fp > 0) payload.push({ beer_id: b.id, storage_area: `cans:${area}`, four_pack: fp, sixth_bbl: 0, half_bbl: 0 });
      }
      for (const area of locAreas.kegs) {
        const sixth = parseFloat(d.kegs?.[area]?.sixth_bbl) || 0;
        const half  = parseFloat(d.kegs?.[area]?.half_bbl)  || 0;
        if (sixth > 0 || half > 0) payload.push({ beer_id: b.id, storage_area: `kegs:${area}`, four_pack: 0, sixth_bbl: sixth, half_bbl: half });
      }
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ location: location.id, session_date: sessionDate, counts: payload, notes }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Failed to submit. Please try again.');
      const data = await res.json().catch(() => ({}));
      const sessionId = data.id;
      // Upload any recount photos attached to flagged beers (best-effort — the
      // count is already saved, so a photo failure must not block completion).
      if (sessionId) {
        for (const [beerId, file] of Object.entries(photos)) {
          try {
            const compressed = await compressImage(file).catch(() => file);
            const fd = new FormData();
            fd.append('beer_id', beerId);
            fd.append('photo', compressed, compressed.name || 'photo.jpg');
            const pres = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-inventory/${sessionId}/photo`, {
              method: 'POST', credentials: 'include', body: fd,
            });
            if (!pres.ok) throw new Error();
          } catch { toast.error("A flagged item's photo didn't upload — the count was still saved."); }
        }
      }
      toast.success('Inventory submitted.');
      setStep('done');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    if (isDirtyRef.current && !window.confirm('You have unsaved counts. Leave anyway?')) return;
    setStep('location');
    setLocation(null);
    setSessionDate(today());
    setBeers([]);
    setCounts({});
    setPrevious(null);
    setNotes('');
    setCurrentArea(null);
    setExpandedBeer(null);
    setPhotos({});
  };

  // ── Shared helpers (available after location is set) ────────────────────
  const locAreas = location ? (STORAGE_AREAS[location.id] || { cans: [], kegs: [] }) : { cans: [], kegs: [] };
  const allAreas = [
    ...locAreas.cans.map(name => ({ name, type: 'cans' })),
    ...locAreas.kegs.map(name => ({ name, type: 'kegs' })),
  ];
  const areaDisplayName = (name, type) => {
    const inBoth = locAreas.cans.includes(name) && locAreas.kegs.includes(name);
    return inBoth ? `${name} (${type === 'cans' ? 'Cans' : 'Kegs'})` : name;
  };
  const areaCountedBeers = (name, type) => {
    if (type === 'cans') return beers.filter(b => parseFloat(counts[b.id]?.cans?.[name]) > 0).length;
    return beers.filter(b => {
      const k = counts[b.id]?.kegs?.[name];
      return (parseFloat(k?.sixth_bbl) || 0) > 0 || (parseFloat(k?.half_bbl) || 0) > 0;
    }).length;
  };

  // ── Location step ────────────────────────────────────────────────────────
  if (step === 'location') {
    return (
      <div className="max-w-lg mx-auto mt-12">
        <h3 className="text-white text-xl font-semibold mb-6 text-center">Select Location</h3>
        <div className="grid grid-cols-2 gap-4">
          {LOCATIONS.map(loc => (
            <button
              key={loc.id}
              onClick={() => selectLocation(loc)}
              className="bg-gray-800 border border-gray-700 rounded-xl p-6 text-white font-semibold text-lg hover:border-orange-500 transition"
            >
              {loc.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Area selection step ──────────────────────────────────────────────────
  if (step === 'area') {
    const orangeCount = beers.reduce((n, b) => n + [cansState(b), kegsState(b)].filter(s => s === 'orange').length, 0);
    const redCount    = beers.reduce((n, b) => n + [cansState(b), kegsState(b)].filter(s => s === 'red').length, 0);
    return (
      <div className="max-w-lg mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white text-xl font-semibold">{location.label}</h3>
            <div className="flex items-center gap-3 mt-1">
              <input type="date" value={sessionDate} onChange={e => setSessionDate(e.target.value)}
                className="bg-gray-700 text-white text-sm rounded px-3 py-1.5 border border-gray-600" />
              {previous && <span className="text-gray-500 text-sm">Last: {previous.session_date}</span>}
            </div>
          </div>
          <button onClick={reset} className="text-sm text-gray-400 hover:text-white">← Change</button>
        </div>

        {canUpload && deliveryCount > 0 && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg px-4 py-2 mb-4 text-blue-300 text-sm">
            📦 {deliveryCount} deliver{deliveryCount === 1 ? 'y' : 'ies'} since last count factored into discrepancy thresholds
          </div>
        )}

        {(orangeCount > 0 || redCount > 0) && (
          <div className="flex gap-2 mb-4">
            {orangeCount > 0 && <div className="flex-1 bg-orange-500/10 border border-orange-500/30 rounded-lg px-4 py-2 text-orange-400 text-sm">⚠ {orangeCount} {orangeCount === 1 ? 'count' : 'counts'} dropped significantly</div>}
            {redCount > 0 && <div className="flex-1 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2 text-red-400 text-sm">↑ {redCount} {redCount === 1 ? 'count' : 'counts'} above expected</div>}
          </div>
        )}

        <p className="text-gray-500 text-xs mb-3 uppercase tracking-wider font-semibold">Choose area to count</p>
        <div className="space-y-2 mb-6">
          {allAreas.map(({ name, type }) => {
            const counted = areaCountedBeers(name, type);
            const done = counted === beers.length && beers.length > 0;
            return (
              <button key={`${type}-${name}`}
                onClick={() => { setCurrentArea({ name, type }); setExpandedBeer(null); setStep('count'); }}
                className="w-full bg-gray-800 border border-gray-700 rounded-xl px-5 py-4 flex items-center justify-between hover:border-orange-500/70 transition"
              >
                <div className="text-left">
                  <div className="text-white font-medium">{areaDisplayName(name, type)}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{type === 'cans' ? '4-packs' : 'kegs'}</div>
                </div>
                <div className={`text-sm font-medium ${done ? 'text-green-400' : counted > 0 ? 'text-yellow-400' : 'text-gray-600'}`}>
                  {done ? '✓ Done' : counted > 0 ? `${counted}/${beers.length}` : '—'}
                </div>
              </button>
            );
          })}
        </div>

        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" rows={2}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm placeholder-gray-500 focus:border-orange-500 focus:outline-none resize-none mb-4" />
        <div className="flex justify-end">
          <button onClick={() => setStep('review')} disabled={beers.length === 0}
            className="px-6 py-2.5 rounded-lg font-semibold text-white disabled:opacity-40"
            style={{ background: '#F05A28' }}>
            Review & Submit
          </button>
        </div>
      </div>
    );
  }

  // ── Count step ───────────────────────────────────────────────────────────
  if (step === 'count' && currentArea) {
    const isCans = currentArea.type === 'cans';
    const areaName = currentArea.name;
    const counted = areaCountedBeers(areaName, currentArea.type);
    const pct = beers.length > 0 ? (counted / beers.length) * 100 : 0;

    return (
      <div className="max-w-lg mx-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <button onClick={() => setStep('area')} className="text-orange-400 text-sm font-medium">← {location.label}</button>
            <h3 className="text-white text-xl font-semibold mt-0.5">{areaDisplayName(areaName, currentArea.type)}</h3>
          </div>
          <div className="text-right flex-shrink-0 ml-4">
            <div className={`text-sm font-semibold ${counted === beers.length && beers.length > 0 ? 'text-green-400' : counted > 0 ? 'text-yellow-400' : 'text-gray-500'}`}>
              {counted}/{beers.length} counted
            </div>
            <div className="w-24 h-1.5 bg-gray-700 rounded-full mt-1.5 overflow-hidden">
              <div className={`h-full rounded-full transition-all ${counted === beers.length && beers.length > 0 ? 'bg-green-400' : 'bg-orange-500'}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>

        {beers.length === 0 && (
          <p className="text-gray-500 text-sm text-center py-12">No beers set up for this location.</p>
        )}

        {/* ── Cans list ── */}
        {isCans && (
          <div>
            {beers.map(b => {
              const val = counts[b.id]?.cans?.[areaName] ?? '';
              const hasCnt = parseFloat(val) > 0;
              return (
                <div key={b.id} className="flex items-center justify-between py-3.5 border-b border-gray-800">
                  <span className={`text-sm flex-1 mr-4 ${hasCnt ? 'text-white font-medium' : 'text-gray-400'}`}>{b.name}</span>
                  <div className="w-24 flex-shrink-0">
                    <WheelCountCell value={val} onSave={q => setCanCount(b.id, areaName, q == null ? '' : String(q))}
                      label="4-Packs" itemName={b.name} allowClear
                      desktopInputClassName="w-full text-center text-lg font-semibold rounded-xl px-2 py-2.5 border bg-gray-800 text-white border-gray-600 focus:outline-none focus:border-orange-500" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Kegs list ── */}
        {!isCans && (
          <div>
            {beers.map(b => {
              const sixth = counts[b.id]?.kegs?.[areaName]?.sixth_bbl ?? '';
              const half  = counts[b.id]?.kegs?.[areaName]?.half_bbl  ?? '';
              const sixthVal = parseFloat(sixth) || 0;
              const halfVal  = parseFloat(half)  || 0;
              const hasCnts  = sixthVal > 0 || halfVal > 0;
              const isOpen   = expandedBeer === b.id;
              const summary  = hasCnts ? [sixthVal > 0 ? `${sixthVal} ⅙` : '', halfVal > 0 ? `${halfVal} ½` : ''].filter(Boolean).join(' · ') : null;
              return (
                <div key={b.id}>
                  <button onClick={() => setExpandedBeer(isOpen ? null : b.id)}
                    className="w-full flex items-center justify-between py-3.5 border-b border-gray-800">
                    <span className={`text-sm text-left flex-1 ${hasCnts ? 'text-white font-medium' : 'text-gray-400'}`}>{b.name}</span>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      {summary ? <span className="text-xs text-gray-300">{summary}</span> : <span className="text-xs text-gray-600">tap to count</span>}
                      <span className="text-gray-600 text-xs">{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="py-4 px-1 flex items-end gap-3 bg-gray-800/50 border-b border-gray-800">
                      <div className="flex-1">
                        <div className="text-gray-400 text-xs mb-2 text-center">1/6 bbl</div>
                        <WheelCountCell value={sixth} onSave={q => setKegCount(b.id, areaName, 'sixth_bbl', q == null ? '' : String(q))}
                          label="1/6 bbl" itemName={b.name} allowDecimal step={0.5} allowClear
                          desktopInputClassName="w-full text-center text-2xl font-bold bg-gray-700 border border-gray-600 rounded-xl py-3 text-white focus:outline-none focus:border-orange-500" />
                      </div>
                      <div className="flex-1">
                        <div className="text-gray-400 text-xs mb-2 text-center">1/2 bbl</div>
                        <WheelCountCell value={half} onSave={q => setKegCount(b.id, areaName, 'half_bbl', q == null ? '' : String(q))}
                          label="1/2 bbl" itemName={b.name} allowDecimal step={0.5} allowClear
                          desktopInputClassName="w-full text-center text-2xl font-bold bg-gray-700 border border-gray-600 rounded-xl py-3 text-white focus:outline-none focus:border-orange-500" />
                      </div>
                      <button onClick={() => setExpandedBeer(null)}
                        className="self-center pb-0.5 text-orange-400 text-sm font-semibold px-2">Done</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="pt-6 pb-2 flex justify-between">
          <button onClick={() => setStep('area')} className="text-sm text-gray-400 hover:text-white px-4 py-2 border border-gray-700 rounded-lg">← All Areas</button>
          <button onClick={() => setStep('area')} className="px-5 py-2 rounded-lg font-semibold text-white text-sm" style={{ background: '#F05A28' }}>Done with this area</button>
        </div>
      </div>
    );
  }

  if (step === 'review') {
    const flagged = getFlaggedBeers();
    const nonZero = beers.filter(b => {
      const t = getTotals(b.id);
      return t.four_pack > 0 || t.sixth_bbl > 0 || t.half_bbl > 0;
    });

    return (
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-white text-xl font-semibold">Review — {location.label}</h3>
          <button onClick={() => setStep('area')} className="text-sm text-gray-400 hover:text-white">← Edit</button>
        </div>

        {flagged.length > 0 && (
          <div className="bg-gray-800/60 border border-orange-500/30 rounded-xl p-4 mb-4">
            <p className="text-orange-400 font-semibold text-sm mb-1">
              ⚠ {flagged.length} {flagged.length === 1 ? 'count looks' : 'counts look'} off — recount &amp; (optionally) add a photo
            </p>
            <p className="text-gray-500 text-xs mb-4">
              Go back to recount, or snap a photo to document the count. Photos are optional and never block submitting.
            </p>
            <div className="space-y-3">
              {flagged.map(fb => (
                <div key={fb.id} className="bg-gray-900 border border-gray-700 rounded-lg p-3">
                  <p className="text-white text-sm font-medium mb-1">{fb.name}</p>
                  {fb.reasons.map((r, i) => (
                    <p key={i} className={`text-xs ${r.state === 'red' ? 'text-red-400' : 'text-orange-400'}`}>
                      {r.dim}: expected {r.expected}, counted {r.curr}{' '}
                      {r.state === 'red'
                        ? `(+${r.diff} above expected — a delivery may not be logged, or a miscount)`
                        : `(−${Math.abs(r.diff)} below expected — dropped more than the floor)`}
                    </p>
                  ))}
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <label className="text-xs text-orange-400 hover:text-orange-300 cursor-pointer">
                      {photos[fb.id] ? '📷 Replace photo' : '📷 Add photo'}
                      <input type="file" accept="image/*" capture="environment" className="hidden"
                        onChange={e => { const f = e.target.files?.[0]; if (f) setPhotos(prev => ({ ...prev, [fb.id]: f })); e.target.value = ''; }} />
                    </label>
                    {photos[fb.id] && (
                      <span className="text-xs text-gray-400 flex items-center gap-2">
                        <span className="truncate max-w-[160px]">✓ {photos[fb.id].name}</span>
                        <button onClick={() => setPhotos(prev => { const n = { ...prev }; delete n[fb.id]; return n; })}
                          className="text-gray-500 hover:text-red-400">✕</button>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden mb-4">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left text-gray-400 text-sm px-4 py-3">Beer</th>
                <th className="text-center text-gray-400 text-sm px-3 py-3">4-Packs</th>
                <th className="text-center text-gray-400 text-sm px-3 py-3">1/6 BBL</th>
                <th className="text-center text-gray-400 text-sm px-3 py-3">1/2 BBL</th>
              </tr>
            </thead>
            <tbody>
              {nonZero.map(b => {
                const t = getTotals(b.id);
                return (
                  <tr key={b.id} className="border-b border-gray-700/50">
                    <td className="px-4 py-2 text-white text-sm">{b.name}</td>
                    <td className="px-3 py-2 text-center text-white text-sm">{t.four_pack}</td>
                    <td className="px-3 py-2 text-center text-white text-sm">{t.sixth_bbl}</td>
                    <td className="px-3 py-2 text-center text-white text-sm">{t.half_bbl}</td>
                  </tr>
                );
              })}
              {nonZero.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500 text-sm">All counts are 0</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {notes && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 mb-4">
            <p className="text-gray-400 text-xs mb-1">Notes</p>
            <p className="text-white text-sm">{notes}</p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button onClick={() => setStep('area')} className="px-5 py-2 rounded-lg text-gray-300 border border-gray-600 hover:border-gray-400">
            Edit
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2 rounded-lg font-semibold text-white disabled:opacity-50"
            style={{ background: '#F05A28' }}
          >
            {submitting ? 'Submitting...' : 'Submit Count'}
          </button>
        </div>
      </div>
    );
  }

  // done
  return (
    <div className="max-w-lg mx-auto mt-16 text-center">
      <div className="text-5xl mb-4">✅</div>
      <h3 className="text-white text-2xl font-semibold mb-2">Count Submitted</h3>
      <p className="text-gray-400 mb-8">{location.label} — {sessionDate}</p>
      <button onClick={reset} className="px-6 py-2 rounded-lg font-semibold text-white" style={{ background: '#F05A28' }}>
        New Count
      </button>
    </div>
  );
}

// ── History Tab ────────────────────────────────────────────────────────────

// Discrepancy photo thumbnail (auth-fetch → blob → object URL), opens full-size in a new tab.
function TaproomPhotoThumb({ sessionId, beerId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = false, obj = null;
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-inventory/${sessionId}/photo?beer_id=${beerId}`, { credentials: 'include' })
      .then(r => (r.ok ? r.blob() : null))
      .then(b => { if (b && !revoked) { obj = URL.createObjectURL(b); setUrl(obj); } })
      .catch(() => {});
    return () => { revoked = true; if (obj) URL.revokeObjectURL(obj); };
  }, [sessionId, beerId]);
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt="Discrepancy" className="h-10 w-10 object-cover rounded border border-gray-700 inline-block" />
    </a>
  );
}

function HistoryTab({ user }) {
  const [sessions, setSessions] = useState([]);
  const [filterLoc, setFilterLoc] = useState('');
  const [selected, setSelected] = useState(null);
  const [photoBeerIds, setPhotoBeerIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const url = `${process.env.REACT_APP_API_URL || ''}/api/taproom-inventory?limit=50${filterLoc ? `&location=${filterLoc}` : ''}`;
    const res = await fetch(url, { credentials: 'include' });
    setSessions(await res.json());
    setLoading(false);
  }, [filterLoc]);

  useEffect(() => { load(); }, [load]);

  const openSession = async (s) => {
    const [res, photoRes] = await Promise.all([
      fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-inventory/${s.id}`, { credentials: 'include' }),
      fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-inventory/${s.id}/photos`, { credentials: 'include' }),
    ]);
    setSelected(await res.json());
    const photos = photoRes.ok ? await photoRes.json() : [];
    setPhotoBeerIds(new Set((Array.isArray(photos) ? photos : []).map(p => p.beer_id)));
  };

  const deleteSession = async (id) => {
    if (!window.confirm('Delete this inventory session?')) return;
    await fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-inventory/${id}`, { method: 'DELETE', credentials: 'include' });
    setSelected(null);
    load();
  };

  const locLabel = (id) => LOCATIONS.find(l => l.id === id)?.label || id;

  if (selected) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-white text-xl font-semibold">{locLabel(selected.location)}</h3>
            <p className="text-gray-400 text-sm mt-0.5">{selected.session_date} · {selected.submitted_by_name}</p>
          </div>
          <div className="flex items-center gap-4">
            {user.role === 'admin' && (
              <button onClick={() => deleteSession(selected.id)} className="text-sm text-red-400 hover:text-red-300">Delete</button>
            )}
            <button onClick={() => setSelected(null)} className="text-sm text-gray-400 hover:text-white">← Back</button>
          </div>
        </div>

        {selected.notes && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 mb-4">
            <p className="text-gray-400 text-xs mb-1">Notes</p>
            <p className="text-white text-sm">{selected.notes}</p>
          </div>
        )}

        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left text-gray-400 text-sm px-4 py-3">Beer</th>
                <th className="text-center text-gray-400 text-sm px-3 py-3">4-Packs</th>
                <th className="text-center text-gray-400 text-sm px-3 py-3">1/6 BBL</th>
                <th className="text-center text-gray-400 text-sm px-3 py-3">1/2 BBL</th>
                <th className="text-center text-gray-400 text-sm px-3 py-3">Photo</th>
              </tr>
            </thead>
            <tbody>
              {selected.counts?.map(c => (
                <tr key={c.beer_id} className="border-b border-gray-700/50">
                  <td className="px-4 py-2 text-white text-sm">{c.name}</td>
                  <td className="px-3 py-2 text-center text-white text-sm">{c.four_pack}</td>
                  <td className="px-3 py-2 text-center text-white text-sm">{c.sixth_bbl}</td>
                  <td className="px-3 py-2 text-center text-white text-sm">{c.half_bbl}</td>
                  <td className="px-3 py-2 text-center">
                    {photoBeerIds.has(c.beer_id) ? <TaproomPhotoThumb sessionId={selected.id} beerId={c.beer_id} /> : <span className="text-gray-700">—</span>}
                  </td>
                </tr>
              ))}
              {(!selected.counts?.length) && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500 text-sm">No counts recorded</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <select
          value={filterLoc}
          onChange={e => setFilterLoc(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-white text-sm rounded px-3 py-2 focus:border-orange-500 focus:outline-none"
        >
          <option value="">All Locations</option>
          {LOCATIONS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="text-gray-500 text-sm">Loading...</p>
      ) : sessions.length === 0 ? (
        <p className="text-gray-500 text-sm">No inventory sessions found.</p>
      ) : (
        <div className="space-y-2">
          {sessions.map(s => (
            <button
              key={s.id}
              onClick={() => openSession(s)}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-5 py-4 flex items-center justify-between hover:border-orange-500/50 transition text-left"
            >
              <div>
                <div className="text-white font-medium">{locLabel(s.location)}</div>
                <div className="text-gray-400 text-sm mt-0.5">{s.submitted_by_name}</div>
              </div>
              <div className="text-right">
                <div className="text-gray-300 text-sm">{s.session_date}</div>
                <div className="text-gray-500 text-xs mt-0.5">
                  {new Date(s.submitted_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Manage Tab (admin) ─────────────────────────────────────────────────────

function ManageTab() {
  const [beers, setBeers] = useState([]);
  const [search, setSearch] = useState('');
  const [filterLoc, setFilterLoc] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLocs, setNewLocs] = useState([]);

  // Settings state
  const [settings, setSettings] = useState(DEFAULT_THRESHOLDS);
  const [settingsSaved, setSettingsSaved] = useState(false);

  const load = async () => {
    const [beersRes, settingsRes] = await Promise.all([
      fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-beers`, { credentials: 'include' }),
      fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-settings`, { credentials: 'include' }),
    ]);
    const beersData = await beersRes.json();
    setBeers(Array.isArray(beersData) ? beersData : []);
    if (settingsRes.ok) {
      const s = await settingsRes.json();
      setSettings({
        four_pack_threshold: parseFloat(s.four_pack_threshold) || 3,
        keg_sixth_equiv_threshold: parseFloat(s.keg_sixth_equiv_threshold) || 3,
      });
    }
  };

  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    await fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(settings),
    });
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2000);
  };

  // Persist a per-SKU floor override ('' clears it → beer falls back to the global default).
  const persistOverride = async (beerId, field, raw) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-beers/${beerId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: raw }),
      });
    } catch { /* optimistic; left as typed */ }
  };

  const toggleLocation = async (beer, loc) => {
    const isActive = beer.locations.includes(loc);
    await fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-beer-locations`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ beer_id: beer.id, location: loc, active: !isActive }),
    });
    load();
  };

  const deleteBeer = async (b) => {
    if (!window.confirm(`Delete "${b.name}"?`)) return;
    await fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-beers/${b.id}`, { method: 'DELETE', credentials: 'include' });
    load();
  };

  const importSheet = async () => {
    setImporting(true);
    setImportMsg('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-beers/import-sheet`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.message) { setImportMsg(`Error: ${data.message}`); return; }
      setImportMsg(`Imported — ${data.beersAdded} new beers, ${data.locationsAdded} location links, ${data.sessionsCreated} baseline sessions created`);
      load();
    } catch {
      setImportMsg('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const addBeer = async () => {
    if (!newName.trim()) return;
    await fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-beers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name: newName.trim(), locations: newLocs }),
    });
    setNewName('');
    setNewLocs([]);
    setAdding(false);
    load();
  };

  const visible = beers.filter(b => {
    const matchSearch = b.name.toLowerCase().includes(search.toLowerCase());
    const matchLoc = !filterLoc || b.locations.includes(filterLoc);
    return matchSearch && matchLoc;
  });

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search beers..."
            className="bg-gray-800 border border-gray-700 text-white text-sm rounded px-3 py-2 w-52 focus:border-orange-500 focus:outline-none"
          />
          <select
            value={filterLoc}
            onChange={e => setFilterLoc(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-white text-sm rounded px-3 py-2 focus:border-orange-500 focus:outline-none"
          >
            <option value="">All Locations</option>
            {LOCATIONS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAdding(true)}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg"
            style={{ background: '#F05A28' }}
          >
            + Add Beer
          </button>
          <button
            onClick={importSheet}
            disabled={importing}
            className="px-4 py-2 text-sm text-gray-300 border border-gray-600 rounded-lg hover:border-gray-400 disabled:opacity-50"
          >
            {importing ? 'Importing...' : 'Import from Sheet'}
          </button>
        </div>
      </div>

      {importMsg && (
        <div className={`border rounded-lg px-4 py-2 mb-4 text-sm ${importMsg.startsWith('Error') ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-green-500/10 border-green-500/30 text-green-400'}`}>
          {importMsg}
        </div>
      )}

      {adding && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl px-5 py-4 mb-4">
          <input
            autoFocus
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addBeer(); if (e.key === 'Escape') setAdding(false); }}
            placeholder="Beer name"
            className="w-full bg-gray-700 text-white text-sm rounded px-3 py-1.5 border border-gray-600 focus:border-orange-500 focus:outline-none mb-3"
          />
          <div className="flex items-center gap-4 mb-3">
            {LOCATIONS.map(l => (
              <label key={l.id} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newLocs.includes(l.id)}
                  onChange={e => setNewLocs(prev => e.target.checked ? [...prev, l.id] : prev.filter(x => x !== l.id))}
                  className="accent-orange-500"
                />
                {l.label}
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={addBeer} className="text-sm text-white px-4 py-1.5 rounded" style={{ background: '#F05A28' }}>Add</button>
            <button onClick={() => { setAdding(false); setNewName(''); setNewLocs([]); }} className="text-sm text-gray-400 hover:text-white px-3 py-1.5">Cancel</button>
          </div>
        </div>
      )}

      {/* ── Discrepancy Settings ── */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 px-6 py-5 mb-6">
        <h4 className="text-white font-semibold mb-1">Discrepancy Detection</h4>
        <p className="text-gray-500 text-xs mb-4">
          Ceiling = last count + deliveries since. A count <span className="text-red-400">above the ceiling</span> is always
          flagged. A count that <span className="text-orange-400">drops more than the floor below the ceiling</span> is flagged
          for a recount. Kegs are measured in 1/6-bbl equivalents (a 1/2 bbl = 3). These are the global defaults — override
          per beer below.
        </p>
        <div className="grid grid-cols-2 gap-3 sm:gap-6">
          {[
            { key: 'four_pack_threshold',        label: 'Cans floor (4-packs)',     hint: 'e.g. 3 = flag if down 3+ four-packs' },
            { key: 'keg_sixth_equiv_threshold',  label: 'Kegs floor (1/6 equiv)',   hint: 'e.g. 3 = flag if down one 1/2 bbl' },
          ].map(({ key, label, hint }) => (
            <div key={key}>
              <label className="block text-gray-400 text-xs mb-1">{label}</label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={settings[key]}
                onChange={e => setSettings(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                className="w-full bg-gray-700 text-white text-sm rounded px-3 py-2 border border-gray-600 focus:border-orange-500 focus:outline-none"
              />
              <p className="text-gray-600 text-xs mt-1">{hint}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={saveSettings}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg"
            style={{ background: '#F05A28' }}
          >
            Save Settings
          </button>
          {settingsSaved && <span className="text-green-400 text-sm">Saved</span>}
        </div>
      </div>

      <p className="text-gray-500 text-xs mb-3">
        Toggle a location to include/exclude a beer from that location's count form. The two floor columns override the global
        discrepancy floors for that beer — leave blank to use the defaults ({settings.four_pack_threshold} / {settings.keg_sixth_equiv_threshold}). {beers.length} beers total.
      </p>

      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-x-auto">
        <table className="w-full min-w-[620px]">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left text-gray-400 text-sm px-3 sm:px-4 py-3">Beer</th>
              {LOCATIONS.map(l => (
                <th key={l.id} className="text-center text-gray-400 text-xs px-2 sm:px-3 py-3 w-16 sm:w-24">{l.label}</th>
              ))}
              <th className="text-center text-gray-400 text-xs px-2 py-3 w-20">Can floor</th>
              <th className="text-center text-gray-400 text-xs px-2 py-3 w-20">Keg floor</th>
              <th className="w-12 sm:w-16" />
            </tr>
          </thead>
          <tbody>
            {visible.map(b => (
              <tr key={b.id} className="border-b border-gray-700/50">
                <td className="px-3 sm:px-4 py-3 text-white text-sm">{b.name}</td>
                {LOCATIONS.map(l => (
                  <td key={l.id} className="px-2 sm:px-3 py-3 text-center">
                    <button
                      onClick={() => toggleLocation(b, l.id)}
                      className={`w-8 h-5 rounded-full transition-colors duration-200 relative ${b.locations.includes(l.id) ? 'bg-orange-500' : 'bg-gray-600'}`}
                    >
                      <span className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-all duration-200 ${b.locations.includes(l.id) ? 'left-4' : 'left-0.5'}`} />
                    </button>
                  </td>
                ))}
                <td className="px-2 py-3 text-center">
                  <input type="number" min="0" step="0.5"
                    value={b.disc_fourpack_threshold ?? ''}
                    placeholder={String(settings.four_pack_threshold)}
                    onChange={e => setBeers(prev => prev.map(x => x.id === b.id ? { ...x, disc_fourpack_threshold: e.target.value === '' ? null : e.target.value } : x))}
                    onBlur={e => persistOverride(b.id, 'disc_fourpack_threshold', e.target.value)}
                    className="w-16 text-center bg-gray-700 text-white text-sm rounded px-2 py-1 border border-gray-600 focus:border-orange-500 focus:outline-none placeholder-gray-500" />
                </td>
                <td className="px-2 py-3 text-center">
                  <input type="number" min="0" step="0.5"
                    value={b.disc_keg_equiv_threshold ?? ''}
                    placeholder={String(settings.keg_sixth_equiv_threshold)}
                    onChange={e => setBeers(prev => prev.map(x => x.id === b.id ? { ...x, disc_keg_equiv_threshold: e.target.value === '' ? null : e.target.value } : x))}
                    onBlur={e => persistOverride(b.id, 'disc_keg_equiv_threshold', e.target.value)}
                    className="w-16 text-center bg-gray-700 text-white text-sm rounded px-2 py-1 border border-gray-600 focus:border-orange-500 focus:outline-none placeholder-gray-500" />
                </td>
                <td className="px-3 py-3 text-center">
                  <button onClick={() => deleteBeer(b)} className="text-xs text-gray-500 hover:text-red-400">Delete</button>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500 text-sm">No beers found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Deliveries Tab ────────────────────────────────────────────────────────

function DeliveriesTab({ user }) {
  const toast = useToast();
  const [view, setView] = useState('list'); // list | upload | preview | done
  const [deliveries, setDeliveries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filterLoc, setFilterLoc] = useState('');
  const [loading, setLoading] = useState(true);

  // Upload / preview state
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const [preview, setPreview] = useState(null); // { location, locationLabel, invoiceNumber, deliveryDate, items }
  const [editedItems, setEditedItems] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Sheet sync state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null); // { imported, skipped, failed, noLocation }

  const locLabel = (id) => LOCATIONS.find(l => l.id === id)?.label || id;

  const loadDeliveries = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL || ''}/api/taproom-deliveries${filterLoc ? `?location=${filterLoc}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      setDeliveries(Array.isArray(data) ? data : []);
    } catch (e) {
      setDeliveries([]);
    }
    setLoading(false);
  }, [filterLoc]);

  useEffect(() => { loadDeliveries(); }, [loadDeliveries]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setParsing(true);
    setParseError('');
    const form = new FormData();
    form.append('pdf', file);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-deliveries/parse-pdf`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      const data = await res.json();
      if (!res.ok) { setParseError(data.message || 'Parse failed'); return; }
      setPreview(data);
      setEditedItems(data.items.map(item => ({ ...item })));
      setDeliveryNotes('');
      setView('preview');
    } catch (err) {
      setParseError('Upload failed — please try again');
    } finally {
      setParsing(false);
      e.target.value = '';
    }
  };

  const updateItem = (i, field, value) => {
    setEditedItems(prev => prev.map((item, idx) =>
      idx === i ? { ...item, [field]: value } : item
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-deliveries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          location: preview.location,
          delivery_date: preview.deliveryDate,
          invoice_number: preview.invoiceNumber,
          notes: deliveryNotes,
          items: editedItems,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Failed to save delivery.');
      toast.success('Delivery saved.');
      setView('done');
      loadDeliveries();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this delivery record?')) return;
    await fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-deliveries/${id}`, { method: 'DELETE', credentials: 'include' });
    loadDeliveries();
  };

  const handleSyncSheet = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-deliveries/sync-sheet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      const data = await res.json();
      setSyncResult(data);
      if (data.imported > 0) loadDeliveries();
    } catch {
      setSyncResult({ error: 'Sync failed — check your connection' });
    } finally {
      setSyncing(false);
    }
  };

  // ── Preview screen ──
  if (view === 'preview' && preview) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-white text-xl font-semibold">
              {preview.locationLabel || 'Unknown Location'} — Delivery Preview
            </h3>
            <p className="text-gray-400 text-sm mt-0.5">
              Invoice {preview.invoiceNumber} · {preview.deliveryDate}
            </p>
          </div>
          <button onClick={() => setView('upload')} className="text-sm text-gray-400 hover:text-white">← Back</button>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden mb-4">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left text-gray-400 text-sm px-4 py-3">Beer</th>
                <th className="text-center text-gray-400 text-sm px-3 py-3 w-24">Format</th>
                <th className="text-center text-gray-400 text-sm px-3 py-3 w-24">Qty</th>
              </tr>
            </thead>
            <tbody>
              {editedItems.map((item, i) => (
                <tr key={i} className="border-b border-gray-700/50">
                  <td className="px-4 py-2">
                    {item.beer_id ? (
                      <span className="text-white text-sm">{item.beer_name}</span>
                    ) : (
                      <span className="text-yellow-400 text-sm">{item.beer_name} ⚠ not in system</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center text-gray-400 text-sm">{item.format}</td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={item.quantity}
                      onChange={e => {
                        const qty = parseFloat(e.target.value) || 0;
                        updateItem(i, 'quantity', qty);
                        updateItem(i, item.format === 'Case' ? 'cases' : item.format === '1/6bbl' ? 'sixth_bbl' : 'half_bbl', qty);
                      }}
                      className="w-16 text-center bg-gray-700 text-white text-sm rounded px-2 py-1 border border-gray-600 focus:border-orange-500 focus:outline-none"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <textarea
          value={deliveryNotes}
          onChange={e => setDeliveryNotes(e.target.value)}
          placeholder="Notes (optional)"
          rows={2}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm placeholder-gray-500 focus:border-orange-500 focus:outline-none resize-none mb-4"
        />

        <div className="flex justify-end gap-3">
          <button onClick={() => setView('upload')} className="px-5 py-2 rounded-lg text-gray-300 border border-gray-600 hover:border-gray-400">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !preview.location}
            className="px-6 py-2 rounded-lg font-semibold text-white disabled:opacity-50"
            style={{ background: '#F05A28' }}
          >
            {saving ? 'Saving...' : 'Confirm Delivery'}
          </button>
        </div>

        {!preview.location && (
          <p className="text-red-400 text-sm mt-2 text-right">Location not detected — check the PDF</p>
        )}
      </div>
    );
  }

  // ── Done screen ──
  if (view === 'done') {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center">
        <div className="text-5xl mb-4">📦</div>
        <h3 className="text-white text-2xl font-semibold mb-2">Delivery Logged</h3>
        <p className="text-gray-400 mb-8">{preview?.locationLabel} · {preview?.deliveryDate}</p>
        <button onClick={() => setView('list')} className="px-6 py-2 rounded-lg font-semibold text-white" style={{ background: '#F05A28' }}>
          Back to Deliveries
        </button>
      </div>
    );
  }

  // ── Detail screen ──
  if (selected) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-white text-xl font-semibold">{locLabel(selected.location)}</h3>
            <p className="text-gray-400 text-sm mt-0.5">
              Invoice {selected.invoice_number} · {selected.delivery_date} · {selected.submitted_by_name}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {user.role === 'admin' && (
              <button onClick={() => handleDelete(selected.id)} className="text-sm text-red-400 hover:text-red-300">Delete</button>
            )}
            <button onClick={() => setSelected(null)} className="text-sm text-gray-400 hover:text-white">← Back</button>
          </div>
        </div>

        {selected.notes && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 mb-4">
            <p className="text-gray-400 text-xs mb-1">Notes</p>
            <p className="text-white text-sm">{selected.notes}</p>
          </div>
        )}

        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left text-gray-400 text-sm px-4 py-3">Beer</th>
                <th className="text-center text-gray-400 text-sm px-3 py-3">Cases</th>
                <th className="text-center text-gray-400 text-sm px-3 py-3">1/6 BBL</th>
                <th className="text-center text-gray-400 text-sm px-3 py-3">1/2 BBL</th>
              </tr>
            </thead>
            <tbody>
              {selected.items?.map((item, i) => (
                <tr key={i} className="border-b border-gray-700/50">
                  <td className="px-4 py-2 text-white text-sm">{item.beer_name}</td>
                  <td className="px-3 py-2 text-center text-white text-sm">{item.cases || '—'}</td>
                  <td className="px-3 py-2 text-center text-white text-sm">{item.sixth_bbl || '—'}</td>
                  <td className="px-3 py-2 text-center text-white text-sm">{item.half_bbl || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── List + upload screen ──
  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <select
          value={filterLoc}
          onChange={e => setFilterLoc(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-white text-sm rounded px-3 py-2 focus:border-orange-500 focus:outline-none"
        >
          <option value="">All Locations</option>
          {LOCATIONS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
        </select>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleSyncSheet}
            disabled={syncing}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-600 text-gray-300 hover:border-gray-400 disabled:opacity-50"
          >
            {syncing ? 'Syncing...' : '↻ Sync from Sheet'}
          </button>
          <label className={`px-4 py-2 rounded-lg text-sm font-medium text-white cursor-pointer ${parsing ? 'opacity-50 pointer-events-none' : ''}`} style={{ background: '#F05A28' }}>
            {parsing ? 'Parsing PDF...' : '+ Upload PDF'}
            <input type="file" accept=".pdf" className="hidden" onChange={handleFileChange} disabled={parsing} />
          </label>
        </div>
      </div>

      {syncResult && !syncResult.error && (
        <div className={`text-sm rounded-lg px-4 py-3 mb-4 ${syncResult.imported > 0 ? 'bg-green-500/10 border border-green-500/30 text-green-400' : 'bg-gray-800 border border-gray-700 text-gray-400'}`}>
          <div>
            {syncResult.imported > 0
              ? `✓ Imported ${syncResult.imported} new deliver${syncResult.imported === 1 ? 'y' : 'ies'}`
              : 'No new deliveries found this week'}
            {syncResult.skipped > 0 && ` · ${syncResult.skipped} already logged`}
            {syncResult.failed?.length > 0 && ` · ${syncResult.failed.length} failed`}
          </div>
          {syncResult.failed?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {syncResult.failed.map((f, i) => (
                <li key={i} className="text-red-400 text-xs">Invoice {f.invoiceNumber || '?'}: {f.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {syncResult?.error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg px-4 py-2 mb-4">
          {syncResult.error}
        </div>
      )}

      {parseError && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg px-4 py-2 mb-4">
          {parseError}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500 text-sm">Loading...</p>
      ) : deliveries.length === 0 ? (
        <p className="text-gray-500 text-sm">No deliveries logged yet. Upload a delivery PDF to get started.</p>
      ) : (
        <div className="space-y-2">
          {deliveries.map(d => (
            <button
              key={d.id}
              onClick={() => setSelected(d)}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-5 py-4 flex items-center justify-between hover:border-orange-500/50 transition text-left"
            >
              <div>
                <div className="text-white font-medium">{locLabel(d.location)}</div>
                <div className="text-gray-400 text-sm mt-0.5">Invoice {d.invoice_number} · {d.submitted_by_name}</div>
              </div>
              <div className="text-right">
                <div className="text-gray-300 text-sm">{d.delivery_date}</div>
                <div className="text-gray-500 text-xs mt-0.5">{d.items?.length} items</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

function TaproomInventory({ user, canUpload, onBack }) {
  const [tab, setTab] = useState('count');
  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
  const countDirtyRef = useRef(false);

  const guardedNavigate = (fn) => {
    if (countDirtyRef.current && !window.confirm('You have unsaved counts. Leave anyway?')) return;
    fn();
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/taproom-settings`, { credentials: 'include' })
      .then(r => r.json())
      .then(s => setThresholds({
        four_pack_threshold: parseFloat(s.four_pack_threshold),
        keg_sixth_equiv_threshold: parseFloat(s.keg_sixth_equiv_threshold),
      }))
      .catch(() => {});
  }, []);

  const tabs = [
    { id: 'count',      label: 'Count' },
    ...(canUpload ? [
      { id: 'history',    label: 'History' },
      { id: 'deliveries', label: 'Deliveries' },
      { id: 'manage',     label: 'Manage Beers' },
    ] : []),
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      <NavBar onBack={() => guardedNavigate(onBack)} />

      <div className="border-b border-gray-700 px-4 sm:px-6">
        <div className="flex flex-wrap gap-4 sm:gap-6 max-w-4xl mx-auto">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => guardedNavigate(() => setTab(t.id))}
              className={`py-4 text-sm font-medium border-b-2 transition ${
                tab === t.id ? 'border-orange-500 text-white' : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <main className="px-4 sm:px-6 py-8">
        {tab === 'count'      && <CountTab user={user} thresholds={thresholds} canUpload={canUpload} onDirtyChange={dirty => { countDirtyRef.current = dirty; }} />}
        {tab === 'history'    && <HistoryTab user={user} />}
        {tab === 'deliveries' && <DeliveriesTab user={user} />}
        {tab === 'manage'     && canUpload && <ManageTab />}
      </main>
    </div>
  );
}

export default TaproomInventory;
